import type { OnboardingDefaults, OnboardingSignals } from "./onboarding.contract";
import { OnboardingLanguage } from "./onboarding.enums";
import {
  DEFAULT_ONBOARDING_COUNTRY,
  countryDefaultLanguage,
  countryFromTimezone,
  normalizeLanguage,
  onboardingCountry,
} from "./onboarding.countries";

/**
 * Resolve the pre-selected country + language from available signals (ADR-0020). Precedence, highest first:
 * URL param > user-touched > client signals > geo-IP > market fallback (DR Congo / French). Country and language
 * are LINKED: when no signal sets the language, it follows the resolved country's default. Pure + deterministic.
 *
 * Refinement vs the ADR's blanket "client > geo": for COUNTRY, geo-IP outranks the timezone hint because a
 * timezone maps ambiguously to a country. For LANGUAGE, the browser language (a client signal) still outranks
 * geo-IP, since it is the user's own stated preference.
 */
export function resolveOnboardingDefaults(signals: OnboardingSignals): OnboardingDefaults {
  const country =
    firstSupportedCountry([signals.urlCountry, signals.userCountry, signals.geoCountry]) ??
    countryFromTimezone(signals.timezone) ??
    DEFAULT_ONBOARDING_COUNTRY;

  const explicitLanguage = firstSupportedLanguage([
    signals.urlLanguage,
    signals.userLanguage,
    signals.browserLanguage,
    signals.geoLanguage,
  ]);

  const language = explicitLanguage ?? countryDefaultLanguage(country);
  return { country, language };
}

function firstSupportedCountry(candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const match = onboardingCountry(candidate);
    if (match) return match.code;
  }
  return undefined;
}

function firstSupportedLanguage(candidates: Array<string | null | undefined>): OnboardingLanguage | undefined {
  for (const candidate of candidates) {
    const match = normalizeLanguage(candidate);
    if (match) return match;
  }
  return undefined;
}
